import { useState, useEffect, useCallback } from "react";
import { supabase } from "@/lib/supabase";

interface Post {
  id: number;
  title: string;
  content: string;
  category?: string;
  sport_type?: string;
  author_id?: string;
  author_nickname?: string;
  views?: number;
  likes?: number;
  created_at: string;
  board_type: "posts" | "notices" | "sports";
  is_important?: boolean;
}

type BoardTab = "전체" | "posts" | "notices" | "sports";

export default function AdminPostsPage() {
  const [posts, setPosts] = useState<Post[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<BoardTab>("전체");
  const [searchQuery, setSearchQuery] = useState("");
  const [message, setMessage] = useState<{ type: "success" | "error"; text: string } | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<Post | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 15;

  const fetchPosts = useCallback(async () => {
    setLoading(true);
    try {
      const results: Post[] = [];

      if (activeTab === "전체" || activeTab === "posts") {
        const { data: communityPosts } = await supabase
          .from("posts")
          .select("id, title, content, category, author_id, views, likes, created_at")
          .order("created_at", { ascending: false })
          .limit(50);

        if (communityPosts) {
          const authorIds = [...new Set(communityPosts.map((p) => p.author_id).filter(Boolean))];
          const authorMap: Record<string, string> = {};

          if (authorIds.length > 0) {
            const { data: profiles } = await supabase
              .from("profiles")
              .select("id, nickname")
              .in("id", authorIds);
            profiles?.forEach((p: { id: string; nickname: string }) => {
              authorMap[p.id] = p.nickname;
            });
          }

          communityPosts.forEach((p) => {
            results.push({
              ...p,
              board_type: "posts" as const,
              author_nickname: p.author_id ? authorMap[p.author_id] || "익명" : "익명",
            });
          });
        }
      }

      if (activeTab === "전체" || activeTab === "notices") {
        const { data: noticesData } = await supabase
          .from("notices")
          .select("id, title, content, author_id, views, created_at, is_important")
          .order("created_at", { ascending: false })
          .limit(50);

        if (noticesData) {
          const authorIds = [...new Set(noticesData.map((n) => n.author_id).filter(Boolean))];
          const authorMap: Record<string, string> = {};

          if (authorIds.length > 0) {
            const { data: profiles } = await supabase
              .from("profiles")
              .select("id, nickname")
              .in("id", authorIds);
            profiles?.forEach((p: { id: string; nickname: string }) => {
              authorMap[p.id] = p.nickname;
            });
          }

          noticesData.forEach((n) => {
            results.push({
              ...n,
              board_type: "notices" as const,
              author_nickname: n.author_id ? authorMap[n.author_id] || "익명" : "익명",
              likes: 0,
            });
          });
        }
      }

      if (activeTab === "전체" || activeTab === "sports") {
        const { data: sportsData } = await supabase
          .from("sports_articles")
          .select("id, title, sport_type, source, views, created_at")
          .order("created_at", { ascending: false })
          .limit(50);

        if (sportsData) {
          sportsData.forEach((s) => {
            results.push({
              ...s,
              board_type: "sports" as const,
              content: s.source || "",
              author_nickname: "스포츠뉴스",
              likes: 0,
            });
          });
        }
      }

      results.sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());

      const filtered = searchQuery.trim()
        ? results.filter(
            (p) =>
              p.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
              (p.author_nickname && p.author_nickname.toLowerCase().includes(searchQuery.toLowerCase())),
          )
        : results;

      setPosts(filtered.slice(0, 100));
    } catch {
      setMessage({ type: "error", text: "게시글을 불러오지 못했습니다" });
    } finally {
      setLoading(false);
    }
  }, [activeTab, searchQuery]);

  useEffect(() => {
    fetchPosts();
    setCurrentPage(1);
  }, [fetchPosts]);

  const totalPages = Math.max(1, Math.ceil(posts.length / itemsPerPage));
  const safePage = Math.min(currentPage, totalPages);
  const paginatedPosts = posts.slice((safePage - 1) * itemsPerPage, safePage * itemsPerPage);

  const handleDelete = useCallback(async () => {
    if (!deleteConfirm) return;

    try {
      const table =
        deleteConfirm.board_type === "notices"
          ? "notices"
          : deleteConfirm.board_type === "sports"
            ? "sports_articles"
            : "posts";

      const { error } = await supabase.from(table).delete().eq("id", deleteConfirm.id);
      if (error) throw error;

      setPosts((prev) => prev.filter((p) => !(p.board_type === deleteConfirm.board_type && p.id === deleteConfirm.id)));
      setMessage({ type: "success", text: `"${deleteConfirm.title}" 게시글이 삭제되었습니다` });
      setDeleteConfirm(null);
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : String(e);
      setMessage({ type: "error", text: `삭제에 실패했습니다: ${msg}` });
    }
  }, [deleteConfirm]);

  const tabs: { key: BoardTab; label: string }[] = [
    { key: "전체", label: "전체" },
    { key: "posts", label: "커뮤니티" },
    { key: "notices", label: "공지사항" },
    { key: "sports", label: "스포츠분석" },
  ];

  const boardLabel = (type: string) => {
    switch (type) {
      case "posts": return "커뮤니티";
      case "notices": return "공지";
      case "sports": return "스포츠분석";
      default: return type;
    }
  };

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h2 className="text-base font-bold text-foreground-800">게시글 관리</h2>
          <p className="text-xs text-foreground-600 mt-0.5">총 {posts.length}건</p>
        </div>

        <div className="relative w-full sm:w-64">
          <span className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center text-foreground-600">
            <i className="ri-search-line text-sm" />
          </span>
          <input
            type="text"
            placeholder="제목 또는 작성자 검색"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full bg-background-100 border border-background-300 rounded-lg pl-9 pr-4 py-2 text-sm text-foreground-700 placeholder-foreground-500 outline-none focus:border-primary-400 transition-colors"
          />
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1.5 flex-wrap">
        {tabs.map((tab) => (
          <button
            key={tab.key}
            onClick={() => setActiveTab(tab.key)}
            className={`px-3.5 py-2 text-xs font-medium rounded-lg border transition-colors whitespace-nowrap cursor-pointer ${
              activeTab === tab.key
                ? "bg-primary-500 text-white border-primary-500"
                : "bg-background-100 text-foreground-600 border-background-300 hover:bg-background-200"
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Message */}
      {message && (
        <div
          className={`px-4 py-3 rounded-lg text-xs font-medium ${
            message.type === "success"
              ? "bg-accent-100/80 text-accent-800 border border-accent-300/60"
              : "bg-red-500/10 text-red-400 border border-red-500/30"
          }`}
        >
          {message.text}
        </div>
      )}

      {/* Delete confirm modal */}
      {deleteConfirm && (
        <div className="bg-background-100 rounded-xl border border-red-500/20 p-5">
          <div className="flex items-start gap-3 mb-4">
            <div className="w-9 h-9 rounded-full bg-red-500/10 flex items-center justify-center flex-shrink-0">
              <i className="ri-alert-line text-red-400" />
            </div>
            <div>
              <h3 className="text-sm font-bold text-foreground-800">게시글 삭제 확인</h3>
              <p className="text-xs text-foreground-600 mt-1">
                "{deleteConfirm.title}" 게시글을 정말 삭제하시겠습니까? 이 작업은 되돌릴 수 없습니다.
              </p>
            </div>
          </div>
          <div className="flex gap-2 justify-end">
            <button
              onClick={() => setDeleteConfirm(null)}
              className="px-4 py-2 bg-background-200 text-foreground-600 text-xs font-medium rounded-lg hover:bg-background-300 transition-colors whitespace-nowrap cursor-pointer"
            >
              취소
            </button>
            <button
              onClick={handleDelete}
              className="px-4 py-2 bg-red-500 text-foreground-50 text-xs font-medium rounded-lg hover:bg-red-600 transition-colors whitespace-nowrap cursor-pointer"
            >
              삭제
            </button>
          </div>
        </div>
      )}

      {/* Posts Table */}
      <div className="bg-background-100 rounded-xl border border-background-300 overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="w-8 h-8 border-2 border-primary-500 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : posts.length === 0 ? (
          <div className="text-center py-20">
            <div className="w-12 h-12 mx-auto mb-3 rounded-full bg-background-200 flex items-center justify-center">
              <i className="ri-article-line text-xl text-foreground-600" />
            </div>
            <p className="text-sm text-foreground-600">게시글이 없습니다</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-background-300 bg-background-200/50">
                  <th className="text-left px-4 py-3 text-xs font-medium text-foreground-600 whitespace-nowrap w-12">#</th>
                  <th className="text-left px-4 py-3 text-xs font-medium text-foreground-600 whitespace-nowrap">게시판</th>
                  <th className="text-left px-4 py-3 text-xs font-medium text-foreground-600 whitespace-nowrap">제목</th>
                  <th className="text-left px-4 py-3 text-xs font-medium text-foreground-600 whitespace-nowrap">작성자</th>
                  <th className="text-center px-4 py-3 text-xs font-medium text-foreground-600 whitespace-nowrap hidden md:table-cell w-16">조회</th>
                  <th className="text-left px-4 py-3 text-xs font-medium text-foreground-600 whitespace-nowrap hidden lg:table-cell w-28">작성일</th>
                  <th className="text-center px-4 py-3 text-xs font-medium text-foreground-600 whitespace-nowrap w-16">삭제</th>
                </tr>
              </thead>
              <tbody>
                {paginatedPosts.map((post) => (
                  <tr
                    key={`${post.board_type}-${post.id}`}
                    className="border-b border-background-300 last:border-0 hover:bg-background-200/40 transition-colors"
                  >
                    <td className="px-4 py-3">
                      <span className="text-[10px] text-foreground-600">{post.id}</span>
                    </td>
                    <td className="px-4 py-3">
                      <span
                        className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium whitespace-nowrap ${
                          post.is_important
                            ? "bg-accent-100/80 text-accent-800"
                            : "bg-background-200 text-foreground-600"
                        }`}
                      >
                        {boardLabel(post.board_type)}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1.5 min-w-0 max-w-[300px]">
                        {post.is_important && (
                          <span className="text-[10px] text-accent-500 font-bold flex-shrink-0">[중요]</span>
                        )}
                        <span className="text-xs text-foreground-700 truncate">{post.title}</span>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-xs text-foreground-600 whitespace-nowrap">{post.author_nickname}</span>
                    </td>
                    <td className="px-4 py-3 text-center hidden md:table-cell">
                      <span className="text-[10px] text-foreground-600">{post.views || 0}</span>
                    </td>
                    <td className="px-4 py-3 hidden lg:table-cell">
                      <span className="text-[10px] text-foreground-600 whitespace-nowrap">
                        {new Date(post.created_at).toLocaleDateString("ko-KR", { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" })}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-center">
                      <button
                        onClick={() => setDeleteConfirm(post)}
                        className="inline-flex items-center justify-center w-7 h-7 rounded-md bg-red-500/10 hover:bg-red-500/20 text-red-400 transition-colors cursor-pointer"
                      >
                        <span className="w-3.5 h-3.5 flex items-center justify-center">
                          <i className="ri-delete-bin-line" />
                        </span>
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {/* Pagination */}
        {posts.length > 0 && totalPages > 1 && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-background-300">
            <span className="text-xs text-foreground-600">
              총 {posts.length}건 중 {(safePage - 1) * itemsPerPage + 1}–{Math.min(safePage * itemsPerPage, posts.length)}건
            </span>
            <div className="flex items-center gap-1">
              <button
                onClick={() => setCurrentPage(1)}
                disabled={safePage === 1}
                className="w-7 h-7 flex items-center justify-center rounded-md text-xs bg-background-200 text-foreground-600 hover:bg-background-300 disabled:opacity-30 disabled:cursor-not-allowed transition-colors cursor-pointer"
              >
                <i className="ri-skip-left-line" />
              </button>
              <button
                onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                disabled={safePage === 1}
                className="w-7 h-7 flex items-center justify-center rounded-md text-xs bg-background-200 text-foreground-600 hover:bg-background-300 disabled:opacity-30 disabled:cursor-not-allowed transition-colors cursor-pointer"
              >
                <i className="ri-arrow-left-s-line" />
              </button>
              {(() => {
                const pages: number[] = [];
                const start = Math.max(1, safePage - 2);
                const end = Math.min(totalPages, safePage + 2);
                for (let i = start; i <= end; i++) pages.push(i);
                return pages.map((p) => (
                  <button
                    key={p}
                    onClick={() => setCurrentPage(p)}
                    className={`w-7 h-7 flex items-center justify-center rounded-md text-xs font-medium transition-colors cursor-pointer whitespace-nowrap ${
                      p === safePage
                        ? "bg-primary-500 text-white"
                        : "bg-background-200 text-foreground-600 hover:bg-background-300"
                    }`}
                  >
                    {p}
                  </button>
                ));
              })()}
              <button
                onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                disabled={safePage === totalPages}
                className="w-7 h-7 flex items-center justify-center rounded-md text-xs bg-background-200 text-foreground-600 hover:bg-background-300 disabled:opacity-30 disabled:cursor-not-allowed transition-colors cursor-pointer"
              >
                <i className="ri-arrow-right-s-line" />
              </button>
              <button
                onClick={() => setCurrentPage(totalPages)}
                disabled={safePage === totalPages}
                className="w-7 h-7 flex items-center justify-center rounded-md text-xs bg-background-200 text-foreground-600 hover:bg-background-300 disabled:opacity-30 disabled:cursor-not-allowed transition-colors cursor-pointer"
              >
                <i className="ri-skip-right-line" />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}